import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

interface VisibilityOptions {
  /**
   * Threshold from 0 to 1 indicating what percentage of the element 
   * must be visible to be considered "visible"
   */
  threshold?: number;
  /**
   * Root margin similar to CSS margin, around the root
   */
  rootMargin?: string;
}

/**
 * Hook for detecting element visibility in viewport
 * Works on web and native platforms
 */
export function useVisibilityDetection(
  options: VisibilityOptions = { threshold: 0.1, rootMargin: '0px' }
) {
  const [isVisible, setIsVisible] = useState(true);
  const ref = useRef<any>(null);

  useEffect(() => {
    // Only use IntersectionObserver on web
    if (Platform.OS === 'web') {
      const element = ref.current;
      if (!element) return;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          // Update our state when observer callback fires
          setIsVisible(entry.isIntersecting);
        },
        {
          threshold: options.threshold,
          rootMargin: options.rootMargin,
        }
      );
      
      observer.observe(element);
      
      return () => {
        observer.unobserve(element);
        observer.disconnect();
      };
    }
    
    // On native, we don't have a reliable way to detect visibility
    // so we default to true and rely on other mechanisms
    return () => {};
  }, [options.threshold, options.rootMargin]);

  return { ref, isVisible };
}
